/**
 * มองตาม: who is looking at whom, and how far they have turned to do it. Purely visual — the
 * simulation never sees it. The player, standing (at ease or ready) with a target, follows it with
 * the eyes first, then turns the head a step if it is well off to the side, and at ease turns round
 * to face it if it is behind. Someone the player has picked out (an NPC, a townsperson, a fellow
 * wanderer) glances back at the player the same way, without turning round.
 *
 * Each turn waits a moment before following and holds until the target has clearly moved past a
 * wider threshold (hysteresis), so a target shifting about a little never makes the eyes jitter.
 * The result is one of a few gaze codes, drawn as cached frames (rig/gaze.ts).
 */
import type { Entity, World } from '@nine/shared';
import type { IsoCamera } from './camera';
import { type Gaze, GAZE_AHEAD, gazeCode, gazePhase } from './rig/gaze';
import type { SpriteSet } from './sprites';

/** Seconds a new direction must hold before the eyes, then the head, then the body follow it. */
const EYES_DELAY = 0.15;
const HEAD_DELAY = 0.4;
const TURN_DELAY = 0.75;
/** Screen pixels to the side before the eyes move off centre (and back under which they return). */
const EYES_IN = 5;
const EYES_OUT = 2;
/** Degrees off the facing before the head turns a step (and back under which it straightens). */
const HEAD_IN = 55;
const HEAD_OUT = 40;
/** Degrees off the facing that count as behind: the body turns round. */
const BEHIND = 115;
/** Tiles: further than this nobody follows anyone with their eyes. */
const PLAYER_RANGE = 16;
const GLANCE_BACK_RANGE = 8;

interface Look {
  targetId: number;
  gaze: Gaze;
  want: Gaze;
  wantSince: number;
  /** A turn round to face the target: the screen heading shown, and the entity's own heading then. */
  turn: number | null;
  turnFrom: number;
  behindSince: number;
}

const AHEAD: Gaze = { x: 0, y: 0, head: 0 };
const same = (a: Gaze, b: Gaze) => a.x === b.x && a.y === b.y && a.head === b.head;
const sgn = (n: number) => (n < 0 ? -1 : n > 0 ? 1 : 0) as -1 | 0 | 1;

/** Screen left/right facing for a screen heading (straight up or down keeps the entity's own). */
function facingFor(h: number, iso: IsoCamera, e: Entity): 1 | -1 {
  if (h === 0 || h === 1 || h === 7) return 1;
  if (h === 3 || h === 4 || h === 5) return -1;
  return iso.facing(e);
}

export class LookAt {
  private readonly looks = new Map<number, Look>();

  /**
   * Follows the player's target (and the target's glance back) for this frame. `height` is how
   * tall an entity is drawn, so a giant is looked up at and a cat down at.
   */
  update(world: World, iso: IsoCamera, height: (e: Entity) => number): void {
    const p = world.player;
    const t = world.get(p.targetId);
    const seen = new Set<number>();
    if (t && t !== p) {
      if (this.follow(p, t, world.time, iso, height, true)) seen.add(p.id);
      const glances =
        t.kind === 'npc' ||
        (t.kind === 'ambient' && !!t.folk) ||
        (t.kind === 'rival' && !world.isHostile(p, t) && t.combatT <= 0);
      if (glances && Math.hypot(t.x - p.x, t.y - p.y) <= GLANCE_BACK_RANGE)
        if (this.follow(t, p, world.time, iso, height, false)) seen.add(t.id);
    }
    for (const id of this.looks.keys()) if (!seen.has(id)) this.looks.delete(id);
  }

  /** The gaze to draw `e` with, or null to draw it as usual. */
  gazeOf(e: Entity): Gaze | null {
    const l = this.looks.get(e.id);
    return l && gazeCode(l.gaze) !== GAZE_AHEAD ? l.gaze : null;
  }

  /**
   * The frame of `e` looking where it looks, standing at ease (or in the ready stance between
   * blows) at the same step of its breath, blinking with the rest; null when it looks straight
   * ahead or cannot (not a rig figure). Until a gaze frame is drawn the plain frame stands in.
   */
  frame(e: Entity, set: SpriteSet, fps: number, time: number): HTMLCanvasElement | null {
    const gaze = this.gazeOf(e);
    if (!gaze || !set.extra || e.dead) return null;
    const ready = e.combatT > 0 && !!set.ready;
    const frames = ready ? set.ready! : set.idle;
    const step = Math.abs(Math.floor(e.animT * fps)) % frames.length;
    const blink = !ready && (time + ((e.id * 7.31) % 40)) % 3.7 < 0.13;
    return set.extra(
      'gaze',
      gazePhase(gazeCode(gaze), ready ? 'ready' : 'idle', step),
      blink,
      () => frames[step]!,
    );
  }

  /** Turned round to face the target: the screen heading and facing to draw `e` with, or null. */
  viewOf(e: Entity, iso: IsoCamera): { heading: number; facing: 1 | -1 } | null {
    const l = this.looks.get(e.id);
    if (l?.turn === null || l?.turn === undefined) return null;
    const h = iso.heading({ heading: l.turn });
    return { heading: h, facing: facingFor(h, iso, e) };
  }

  /** One looker's next step toward looking at `t`; false when it is in no state to look. */
  private follow(
    e: Entity,
    t: Entity,
    now: number,
    iso: IsoCamera,
    height: (e: Entity) => number,
    canTurn: boolean,
  ): boolean {
    const busy =
      e.dead ||
      t.dead ||
      e.moving ||
      e.z > 0.5 ||
      !!e.cast ||
      e.swingT > 0 ||
      e.spinT > 0 ||
      e.dashT > 0 ||
      e.stunT > 0 ||
      e.hitT > 0 ||
      e.blocking ||
      e.meditating ||
      !!e.emote ||
      !!e.mount;
    if (busy || Math.hypot(t.x - e.x, t.y - e.y) > (canTurn ? PLAYER_RANGE : GLANCE_BACK_RANGE)) return false;
    let l = this.looks.get(e.id);
    if (!l || l.targetId !== t.id) {
      l = {
        targetId: t.id,
        gaze: AHEAD,
        want: AHEAD,
        wantSince: now,
        turn: null,
        turnFrom: e.heading,
        behindSince: -1,
      };
      this.looks.set(e.id, l);
    }
    // The entity turned of its own accord (a step, a blow): its own heading wins again.
    if (l.turn !== null && e.heading !== l.turnFrom) l.turn = null;
    // (A turn is kept as a heading in the world, like the entity's own, so it survives a camera turn.)
    const h = iso.heading(l.turn === null ? e : { heading: l.turn });
    const [ex, ey] = iso.proj(e.x, e.y);
    const [tx, ty] = iso.proj(t.x, t.y);
    const a = (Math.atan2(ty - ey, tx - ex) * 180) / Math.PI;
    const off = Math.abs(((((a - h * 45) % 360) + 540) % 360) - 180);
    const side = (tx - ex) * facingFor(h, iso, e);

    // Behind: at ease, after a moment, turn round to face it (the eyes start over from ahead).
    if (canTurn && e.combatT <= 0 && off > BEHIND) {
      if (l.behindSince < 0) l.behindSince = now;
      if (now - l.behindSince >= TURN_DELAY) {
        l.turn = (((Math.round(a / 45 - iso.octants) % 8) + 8) % 8);
        l.turnFrom = e.heading;
        l.behindSince = -1;
        l.gaze = l.want = AHEAD;
        l.wantSince = now;
        return true;
      }
    } else l.behindSince = -1;

    // What the eyes and head would do now, held where they are until clearly past the line.
    const cur = l.gaze;
    const dir = sgn(side);
    const eyesX = Math.abs(side) > EYES_IN || (cur.x === dir && Math.abs(side) > EYES_OUT) ? dir : 0;
    const head =
      eyesX && (off > HEAD_IN || (cur.head === eyesX && off > HEAD_OUT)) && off <= BEHIND ? eyesX : 0;
    const k = height(t) / Math.max(1, height(e));
    const near = Math.hypot(t.x - e.x, t.y - e.y);
    const y =
      k > 1.35 || (cur.y === -1 && k > 1.2) ? -1 : (k < 0.6 || (cur.y === 1 && k < 0.7)) && near < 5 ? 1 : 0;
    const want: Gaze = { x: head || eyesX, y, head };
    if (!same(want, l.want)) {
      l.want = want;
      l.wantSince = now;
    }
    // The eyes go first, the head follows a moment later.
    const waited = now - l.wantSince;
    const next: Gaze = { ...l.gaze };
    if (waited >= EYES_DELAY) {
      next.x = want.x;
      next.y = want.y;
    }
    if (waited >= HEAD_DELAY) next.head = want.head;
    l.gaze = next;
    return true;
  }
}
